import {
  DUEL_LEVELS,
  DUEL_STAT_PROFILES,
  skillProfile,
  type BoardDef,
  type CharacterDef,
  type Roster,
} from '@archon3d/core';
import { usePortraits } from '../../editor/portraits';
import { RULE_BOOK_SECTIONS, attackLabel, rulePage, rulebookOrder } from '../ruleBook';

interface Props {
  roster: Roster;
  /** The board the characters start on: it tells which Light and Dark pieces are counterparts. */
  board?: BoardDef;
  onBack: () => void;
}

const pct = (value: number | undefined) => `${Math.round((value ?? 0) * 100)}%`;
const number = (value: number | undefined, fallback = 1) => (value ?? fallback).toFixed(2);

const levelColumns = [
  ['hitPoints', 'HP'],
  ['damage', 'Damage'],
  ['attackInterval', 'Interval'],
  ['effectChance', 'Effect'],
  ['protection', 'Protection'],
] as const;

function CharacterCard({ character, portrait }: { character: CharacterDef; portrait?: string }) {
  const resistances = Object.entries(character.combat?.resistances ?? {});
  return (
    <article className="rule-character" aria-label={character.name}>
      <header className="rule-character-head">
        <div className="rule-character-portrait">
          {portrait ? (
            <img src={portrait} alt={`${character.name}, front view`} />
          ) : (
            <span aria-hidden="true">{character.name.slice(0, 1)}</span>
          )}
        </div>
        <div>
          <h3>{character.name}</h3>
          <p>
            Tier {character.tier}
            {character.summoned ? ' · Summoned element' : ''}
          </p>
        </div>
      </header>
      <dl className="rule-character-stats">
        <div>
          <dt>HP</dt>
          <dd>{Math.round(character.hitPoints)}</dd>
        </div>
        <div>
          <dt>Damage</dt>
          <dd>{Math.round(character.attack.damage)}</dd>
        </div>
        <div>
          <dt>Power</dt>
          <dd>{character.force ?? '—'}</dd>
        </div>
      </dl>
      <dl className="rule-character-details">
        <dt>Movement</dt>
        <dd>
          {character.movement.type} · {character.movement.range} cells
        </dd>
        <dt>Arena speed</dt>
        <dd>×{number(character.combat?.speed)}</dd>
        <dt>Attack</dt>
        <dd>
          {attackLabel(character)} · {character.attack.damageType ?? 'physical'}
        </dd>
        <dt>Attack interval</dt>
        <dd>{number(character.attack.interval)} s</dd>
        <dt>Effect chance</dt>
        <dd>{pct(character.attack.effectChance ?? 1)}</dd>
        <dt>Protection</dt>
        <dd>{pct(character.combat?.protection)}</dd>
        {resistances.length > 0 && (
          <>
            <dt>Resistances</dt>
            <dd>{resistances.map(([type, value]) => `${type} ${pct(value)}`).join(', ')}</dd>
          </>
        )}
        {!!(character.spellPoints || character.spells?.length) && (
          <>
            <dt>Magic</dt>
            <dd>
              {character.spellPoints ?? 0} points · {character.spells?.join(', ') || 'no spells'}
            </dd>
          </>
        )}
      </dl>
      <div
        className="rule-level-scroll"
        role="region"
        aria-label={`${character.name} difficulty multipliers`}
        tabIndex={0}
      >
        <table className="rule-levels">
          <caption>Difficulty multipliers · Expert baseline</caption>
          <thead>
            <tr>
              <th scope="col">Level</th>
              {levelColumns.map(([key, label]) => (
                <th scope="col" key={key}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DUEL_LEVELS.map(({ id, label }) => {
              const shared = DUEL_STAT_PROFILES[id];
              const own = id === 'expert' ? undefined : character.combat?.levels?.[id];
              return (
                <tr key={id}>
                  <th scope="row">{label}</th>
                  {levelColumns.map(([key]) => (
                    <td key={key}>×{number(shared[key] * (own?.[key] ?? 1))}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </article>
  );
}

export function RuleBookPanel({ roster, board, onBack }: Props) {
  const portraits = usePortraits(roster);
  const characters = rulebookOrder(roster, board);
  return (
    <div className="rule-book" data-testid="rule-book">
      <header className="rule-book-head">
        <div>
          <h2 className="menu-heading">Rule Book</h2>
          <p className="menu-text">RPG-Arena board, combat, characters and player guide.</p>
        </div>
        <button onClick={onBack}>Close</button>
      </header>

      <nav className="rule-index" aria-label="Rule book sections">
        <a href="#rules-play">Rules</a>
        <a href="#rules-characters">Characters</a>
        <a href="#rules-tips">Tips</a>
        <a href="#rules-create">Create games</a>
        <a href="#rules-start">Play & online</a>
        <a href="#rules-account">Account & audio</a>
      </nav>

      <section id="rules-play">
        <h3>Rules of play</h3>
        {RULE_BOOK_SECTIONS.play.map(rulePage).map((page) => (
          <div key={page.title}>
            <h4>{page.title}</h4>
            <ul>
              {page.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ))}
        <p>
          On the 9×9 original, Light and Dark contest the central power points. Hex Crown supports
          two to six independent factions. You win by holding every power point or by becoming the
          last faction with pieces. Engagement timing and allowed combat modes can be changed in an
          edited game.
        </p>
        <h4>Arena controls</h4>
        <p>
          Keyboard/touch and ordinary gamepads move in screen directions, and the fighter turns the
          way it moves. With a mouse, an attack turns it toward the cursor first. On Quest, either
          stick moves the fighter, either trigger attacks and A or X dashes; hold a grip to point at
          the radio and VR controls instead, since the trigger fights. On a PC pad the bottom and
          left face buttons fire (B and Y on an 8BitDo or Nintendo layout, A and X on an Xbox one),
          as do the right trigger and bumper; the right and top face buttons dash, as do the left
          trigger and bumper. With both sticks pushed, on a pad or on Quest, the left one moves the
          fighter and the right one aims it, so it can back away facing its foe. Hold fire and use
          the right stick to aim a Wizard or Sorceress projectile; while it is airborne, left/right
          bends it up to a limited angle. A blow that lands is felt in the controller.
        </p>
        <h4>Board with a gamepad</h4>
        <p>
          On a PC a game controller works the board beside the mouse. A ring marks the cell the
          mouse points at; the left stick or D-pad moves that ring cell by cell, the bottom face
          button (A on an Xbox layout, B on an 8BitDo one) or the right trigger picks it like a
          click, and the right face button lets go of a selected piece. The right stick turns and
          tilts the table; pressed in, up and down zoom and left and right pan. The bumpers zoom
          too, RB in and LB out, and a pad without a right stick holds LT and uses the D-pad
          instead. Back moves the pad between the board, the wooden tablets, any message beside the
          board and the sound panel in the corner, where the D-pad walks the radio buttons and the
          two sliders, the bottom button presses a station and the bumpers turn a slider up and
          down; a question that stops play takes the pad until it is answered, with the bottom
          button on the framed control and the right one for the safe way out. Start opens and
          closes the menu. The browser keeps a pad hidden until a button is pressed; the Settings
          page shows what it sees, and an 8BitDo pad wants its X (Xbox) mode.
        </p>
        <h4>Playing together at one computer</h4>
        <p>
          Seat two or more people in the setup and choose how they share the computer. With
          <strong> Take turns</strong>, one device is passed around: a screen asks for the next
          person before each turn, before a secret support choice in Card mode and before a Muster
          answer, and the AI fights every duel for the people (Card, Roll or Watch). With
          <strong> A controller each</strong>, every person picks a controller beside their name,
          the mouse and keyboard for one of them and a gamepad for each other; on the board only the
          controller of the person on turn moves the ring, and in the Arena the two people fight
          their own duel on one screen, each with their own controls. A pad gives the duel up by
          holding Start; the panel over the arena has a Forfeit button per fighter. Playing from
          different devices is Host game and Join game.
        </p>
        <h4>AI Combat levels</h4>
        <ul>
          {DUEL_LEVELS.map(({ id, label, skill }) => {
            const profile = skillProfile(skill);
            return (
              <li key={id}>
                {label}: movement {Math.round(profile.moveScale * 100)}%, aim within about{' '}
                {Math.round((profile.aimSpread * 180) / Math.PI)}°, combat power{' '}
                {Math.round(DUEL_STAT_PROFILES[id].powerGoal * 100)}% of Expert.
              </li>
            );
          })}
        </ul>
      </section>

      <section id="rules-characters">
        <h3>Characters and elements</h3>
        <p>
          Power is the broad battlefield rating. Arena outcomes also depend on speed, protection,
          damage type, resistance, effect chance, level and player skill. HP and Damage are rounded
          to whole numbers for readability. Attack interval is the time between one attack and the
          next. Characters are listed by tier, Light and Dark counterparts side by side, with the
          summoned elements closing their tier. Difficulty multipliers include each character’s
          adjustments; a lower interval multiplier means faster attacks.
        </p>
        <div className="rule-character-grid">
          {characters.map((character) => (
            <CharacterCard
              key={character.id}
              character={character}
              portrait={portraits[character.id]}
            />
          ))}
        </div>
      </section>

      <section id="rules-tips">
        <h3>Tips</h3>
        <ul>
          {rulePage(RULE_BOOK_SECTIONS.tips).lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p>
          Arena: keep moving, use cover and do not waste a dash. Card: preserve flexible support
          cards and account for visible faction bonuses. Roll: the stronger character is favoured,
          never guaranteed. Against several factions, avoid looking strongest too early.
        </p>
      </section>

      <section id="rules-create">
        <h3>Creating your own games</h3>
        <p>
          Open Editor, create or copy a game, then define its square or hex board, factions,
          placements, power points, roster, arenas and allowed combat modes. Give every faction a
          legal opening position and comparable total power. Test movement routes, multi-piece
          engagement rules, card support limits and every arena spawn before publishing. Private
          saves remain yours; publishing makes a version available to other players without
          overwriting the original templates.
        </p>
      </section>

      <section id="rules-start">
        <h3>Starting, hosting and joining</h3>
        <ul>
          {rulePage(RULE_BOOK_SECTIONS.start).lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p>
          In New Game choose Human, AI or Out for every faction, then set Tactics and Combat level.
          Combat modes are Card, Roll, Arena and Watch. Card is the default, with the original
          square game selected for a new game. A hosted game gives a short room code; each person
          joins on their own device and controls one faction. Radio choice and volume are local to
          each player.
        </p>
      </section>

      <section id="rules-account">
        <h3>Accounts, saves, recommendations and radio</h3>
        <ul>
          {rulePage(RULE_BOOK_SECTIONS.account).lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p>
          Sound controls effects; Music controls the selected radio station. Muting sound greys the
          game-view sliders so the silent state is visible. Settings are available from the home
          page, editor and a running game.
        </p>
      </section>

      <div className="menu-actions">
        <button onClick={onBack}>Close rule book</button>
      </div>
    </div>
  );
}
